import { createCrudPage } from './factories/crud-page.js';
import { renderDate } from '../core/grid/column-renderers.js';
import { createFbFormIdCodec } from './factories/fb-form-id-codec.js';

const basePath = '/article';

/** @type {import('../contracts/page.js').PageMountFn} */
export default function mountArticlePage(container, services) {
  return createCrudPage({
    title: 'Articles',
    basePath,
    columns: [
      { key: 'article_date', label: 'Date', sortable: true, renderHtml: true,
        render: (v) => renderDate(v, services.localeConfig) },
      { key: 'article_title', label: 'Title', sortable: true },
      { key: 'article_www', label: 'Active', sortable: true,
        render: (v) => v == 1 ? 'Yes' : 'No' },
      { key: 'articletype_name', label: 'Type', sortable: true },
      { key: 'companytype_name', label: 'Sector', sortable: true },
    ],
    fields: [
      { key: 'article_title', label: 'Article title', type: 'text', required: true },
      { key: 'article_www', label: 'Active', type: 'yesno', defaultValue: '0', numeric: true },
      { key: 'article_date', label: 'Publication date', type: 'date' },
      { key: 'author', label: 'Author', type: 'multi-relation',
        engine: 'searchable-multi', optionsUrl: '/authors/options' },
      { key: 'articletype_id', label: 'Article type', type: 'async-select',
        optionsUrl: '/articletype/options',
        iconProp: 'icon',
        // Numeric FK; no articletype id of 0 exists, so an empty selection omits the key.
        numericId: true,
        placeholder: 'Select article type' },
      { key: 'companytype_id', label: 'Sector', type: 'async-select',
        optionsUrl: '/company-type/options',
        // Numeric FK; companytype_id 0 is the real "Unassigned" row, so empty → 0.
        numericId: true, numericIdEmptyValue: 0,
        placeholder: 'Select sector' },
      { key: 'company', label: 'Brands described in article', type: 'multi-relation',
        engine: 'searchable-multi', optionsUrl: '/company/options' },
      { key: 'square', label: 'Square photo', type: 'file', accept: 'image/*', photo: true,
        fieldInfo: 'Mandatory photo. Used for home page and article list.' },
      { key: 'gallery', label: 'Gallery photo', type: 'file', multiple: true, accept: 'image/*', photo: true,
        fieldInfo: 'Insert at least one gallery photo. Description is mandatory' },
      { key: 'vip', label: 'Rectangle (VIP) photo', type: 'file', accept: 'image/*', photo: true },
      { key: 'videos', label: 'YouTube Videos', type: 'videos',
        fieldInfo: 'Add YouTube videos with optional title and description. Drag to reorder.' },
      { key: 'article_intro', label: 'Article intro', type: 'textarea' },
      { key: 'article_text', label: 'Article text', type: 'rich-text' },
      { key: 'learn_about', label: 'Learn about', type: 'rich-text' },
      { key: 'conclusions', label: 'Conclusions', type: 'rich-text' },
      { key: 'figures_text', label: 'Figures text', type: 'text' },
      { key: 'figures_number', label: 'Figures number', type: 'text' },
      { key: 'fb_savedforms_id', label: 'Form', type: 'form-link',
        optionsUrl: '/fb-savedforms/options',
        allowClear: true,
        placeholder: 'Select form',
        // Pick an existing form OR "Create new form" (opens the form-builder modal via formPicker).
        // '' (cleared) ⇄ 0 (legacy "no form" sentinel). resolveLabel is auto-generated by the
        // factory from optionsUrl, so the saved form's name shows in edit mode.
        codec: createFbFormIdCodec() },
    ],
    filters: [
      { key: 'title', label: 'Title', type: 'text',
        placeholder: 'Please start typing' },
      { key: 'articleType', label: 'Article Type', type: 'select',
        optionsUrl: '/articletype/options',
        placeholder: 'Select article type' },
      { key: 'author', label: 'Author', type: 'select',
        optionsUrl: '/authors/options',
        placeholder: 'Select author' },
      { key: 'companyType', label: 'Sector', type: 'select',
        optionsUrl: '/company-type/options',
        placeholder: 'Select sector' },
      { key: 'companyArticle', label: 'Brand', type: 'select',
        optionsUrl: '/company/options',
        placeholder: 'Select brand from catalogue' },
      { key: 'dateFrom', label: 'Date From', type: 'date',
        placeholder: 'Pick start date', customPicker: true },
      { key: 'dateTo', label: 'Date To', type: 'date',
        placeholder: 'Pick end date', customPicker: true },
      { key: 'active', label: 'Active', type: 'radio',
        options: [
          { value: '', label: 'All' },
          { value: '1', label: 'Yes' },
          { value: '0', label: 'No' },
        ] },
    ],
    rowActions: [
      { key: 'edit', label: 'Edit' },
      { key: 'activate', label: 'Activate', icon: 'activate',
        confirm: 'Are you sure you want to activate this article?',
        visible: (row) => row.article_www == 0 },
      { key: 'deactivate', label: 'Deactivate', icon: 'deactivate',
        confirm: 'Are you sure you want to deactivate this article?',
        visible: (row) => row.article_www == 1 },
      { key: 'duplicate', label: 'Duplicate', icon: 'duplicate',
        confirm: 'Are you sure you want to duplicate this article?' },
      { key: 'delete', label: 'Delete', variant: 'danger',
        confirm: 'Are you sure you want to remove this row?' },
    ],
    customActions: (ctx) => ({
      activate: async (row) => {
        const id = String(row?.[ctx.rowKey] ?? '').trim();
        if (!id) return;
        await ctx.services.apiClient.patch(
          `${basePath}/${encodeURIComponent(id)}`, { article_www: 1 },
          { signal: ctx.services.signal },
        );
        ctx.grid.refresh();
      },
      deactivate: async (row) => {
        const id = String(row?.[ctx.rowKey] ?? '').trim();
        if (!id) return;
        await ctx.services.apiClient.patch(
          `${basePath}/${encodeURIComponent(id)}`, { article_www: 0 },
          { signal: ctx.services.signal },
        );
        ctx.grid.refresh();
      },
      duplicate: async (row) => {
        const id = String(row?.[ctx.rowKey] ?? '').trim();
        if (!id) return;
        // Pass the page signal so the fetch is cancelled on navigation/teardown,
        // and bail before reopening the editor if it was aborted in the meantime
        // (otherwise we'd reopen a destroyed editor after the user navigated away).
        const data = await ctx.adapter.get(id, ctx.services.signal);
        if (ctx.services.signal?.aborted) return;
        const prefill = { ...data, article_www: 0 };
        delete prefill.id;
        delete prefill.created;
        ctx.editor.open(undefined, { prefill });
      },
    }),
    defaultSort: { field: 'article_date', dir: 'desc' },
    filterBar: { applyButton: true },
  })(container, services);
}
